// CHA-582 / W1 — Work-layer list view model (platform).
// CHA-592 / W2b — runs now join the same queue, tagged by KIND ('run' vs finding).
//
// Groups findings AND runs into the canonical Work-list order. Ron's ruling: the list is GROUPED
// BY STATE with OVERDUE FIRST. The two state machines (finding vs run — runModel.js) are DISTINCT,
// and the only literal they share is 'open': a finding's open ("not sent") is a different KIND from
// a run's initial open. So grouping keys on kind, not state alone — a run in state `open` lands in
// its own "Run · open" group and never in the finding "Open / not sent" group.
//
// This is a PLATFORM view model: it takes a plain array of items and returns ordered groups. Each
// item may carry `kind: 'run'` (a run) or no kind (a finding). The tenant shell (W1b/W2b) mounts it
// into the Work surface. Pure — no Firestore, no React — so it is unit-testable in isolation.

// Finding states (mirror of functions/platform/runModel.js; the detector's 'new' is an alias of
// 'open'). Kept minimal on purpose — run states never live on a finding (W0 correction).
const FINDING_STATE_ALIASES = { new: 'open' };

// Canonical group order — OVERDUE FIRST. The non-terminal RUN groups precede the finding groups so
// an active run (any of overdue / pending_verification / waiting_reply / run-open) sorts above every
// open finding; within the run groups the order is by run-urgency, with run-open (the least-advanced
// run state) last among them. Finding groups follow; the terminal `done` run sorts last.
const GROUP_ORDER = [
  { key: 'overdue', label: 'Overdue' },
  { key: 'pending_verification', label: 'Pending verification' },
  { key: 'waiting_reply', label: 'Waiting on reply' },
  // CHA-592 — a run created by start_run begins in state 'open' (RUN_INITIAL_STATE). It is a RUN,
  // not a finding, so it gets its own group placed with the other (active) run groups, ahead of the
  // finding "Open / not sent" group.
  { key: 'run_open', label: 'Run · open' },
  { key: 'open', label: 'Open / not sent' },
  { key: 'snoozed', label: 'Snoozed' },
  { key: 'closed', label: 'Closed' },
  // Terminal run state (W2). Listed so a `done` run lands in its own group instead of falling
  // through stateKey's default and being miscounted as an open finding.
  { key: 'done', label: 'Done' },
];
// The two state machines are DISJOINT partitions of GROUP_ORDER. Grouping resolves an item ONLY
// within its own kind's set: a FINDING can never land in a run group, and a RUN can never land in a
// finding group — even if a doc carries the other machine's state (a bug we want visibly bucketed as
// its own kind's fallback, not silently misfiled across machines).
const FINDING_GROUP_KEYS = new Set(['open', 'snoozed', 'closed']);
const RUN_GROUP_KEYS = new Set(['overdue', 'pending_verification', 'waiting_reply', 'run_open', 'done']);

// Map an item to its group key, keyed on item.kind so the two machines never cross:
//   - a RUN resolves only among run-state groups. Its initial state 'open' maps to the distinct
//     run_open group; a run carrying a finding-only state (snoozed/closed) or anything unknown falls
//     back to run_open — never a finding bucket.
//   - a FINDING resolves only among finding-state groups. A finding carrying a run-only state
//     (overdue/waiting_reply/pending_verification/run_open/done) or anything unknown falls back to
//     the finding 'open' group — never a run bucket.
function stateKey(item) {
  const isRun = item?.kind === 'run';
  const raw = String(item?.state == null ? 'open' : item.state).trim();
  const norm = FINDING_STATE_ALIASES[raw] || raw;
  if (isRun) {
    if (norm === 'open') return 'run_open';
    return RUN_GROUP_KEYS.has(norm) ? norm : 'run_open';
  }
  return FINDING_GROUP_KEYS.has(norm) ? norm : 'open';
}

function exposureOf(item) {
  const n = Number(item?.evidence?.gross_ext_price);
  return Number.isFinite(n) ? n : 0;
}

/**
 * groupFindings(items, { includeEmpty = false }) — ordered groups for the Work list.
 * Returns [{ key, label, items, count, exposure }]. Overdue-first; empty groups are dropped unless
 * includeEmpty is set (the header still renders their order when items arrive).
 */
function groupFindings(items = [], { includeEmpty = false } = {}) {
  const buckets = new Map(GROUP_ORDER.map((g) => [g.key, []]));
  for (const item of Array.isArray(items) ? items : []) {
    buckets.get(stateKey(item)).push(item);
  }
  const groups = [];
  for (const { key, label } of GROUP_ORDER) {
    const bucket = buckets.get(key);
    if (!bucket.length && !includeEmpty) continue;
    // Within a group, rank by exposure desc (biggest dollars first — the COO scan).
    bucket.sort((a, b) => exposureOf(b) - exposureOf(a));
    groups.push({
      key,
      label,
      items: bucket,
      count: bucket.length,
      exposure: bucket.reduce((sum, it) => sum + exposureOf(it), 0),
    });
  }
  return groups;
}

module.exports = { groupFindings, GROUP_ORDER, stateKey, exposureOf };
